import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Input from "@/components/input";
import { BRAND_ITEM_FIXED_VALUE, BRAND_ITEM_VARIABLE_VALUE } from "@/const";
import isInValueRange from "@/utils/isValueInRange";
import handleVariableAmountChange from "@/utils/handleVariableAmountChange";
import { useCartContext } from "@/context/CartContext";
import Button from "@/components/button";
import convertToPoints from "@/utils/convertToPoints";
import CardImage from "@/components/cardImage";
import { useGiftCardCatalogContext } from "@/context/GiftCardCatalogContext";
import { TangoBrand, TangoProduct } from "@t3/shared";
import { AddMaxValueButton } from "@/components/AddMaxValueButton";

export const CardInfo = () => {
  const { brandKey } = useParams();
  const { catalog, catalogLoading, catalogError } = useGiftCardCatalogContext();
  const [card, setCard] = useState<TangoBrand | null>(null);
  const [variableValue, setVariableValue] = useState("");
  const [utid, setUtid] = useState("");
  const [inputWarning, setInputWarning] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { addTangoItem } = useCartContext();

  const navigate = useNavigate();

  useEffect(() => {
    const getCard = async () => {
      const foundCard = catalog?.find((brand) => brand.brandKey === brandKey);
      setCard(foundCard || null);
    };
    if (catalog) {
      getCard();
    }
  }, [catalog, brandKey]);

  if (catalogLoading || !card) {
    return <div>Loading...</div>;
  }

  const hasVariableValueItem = card?.items?.find(
    (item) => item.valueType === BRAND_ITEM_VARIABLE_VALUE,
  );
  const hasFixedValueItem = card?.items?.find(
    (item) => item.valueType === BRAND_ITEM_FIXED_VALUE,
  );
  const hasOnlyVariableValue =
    hasVariableValueItem && card?.items?.length === 1;
  const selectedItem = card?.items?.find((item) => item.utid === utid);
  const utidIsVariable = selectedItem?.valueType === BRAND_ITEM_VARIABLE_VALUE;

  if (catalogError) {
    throw new Error(
      "Sorry, we encountered an issue loading the gift card information. Please try again later.",
    );
  }

  const handleSubmit = async () => {
    setSubmitting(true);
    const submittedUtid = hasOnlyVariableValue ? card.items[0].utid : utid;
    const item: TangoProduct = {
      utid: submittedUtid,
      amount: Number(variableValue) || (selectedItem?.faceValue ?? 0),
      type: variableValue ? BRAND_ITEM_VARIABLE_VALUE : BRAND_ITEM_FIXED_VALUE,
      metadata: card,
    };
    await addTangoItem(item);

    console.log("added to cart", item);
    console.log("attempt redirect back to shop");
    navigate("/shop/giftCards");
  };

  const checkValueIsInRange = (minValue: number, maxValue: number) => {
    if (!minValue && !maxValue) {
      if (variableValue) {
        return false;
      }
    }

    if (isInValueRange(variableValue, minValue, maxValue)) {
      return false;
    }

    return true;
  };

  const isSubmitDisabled = () => {
    if (hasOnlyVariableValue) {
      const { minValue, maxValue } = card.items[0];
      return checkValueIsInRange(minValue ?? 0, maxValue ?? 0);
    } else if (utidIsVariable) {
      const { minValue, maxValue } = selectedItem;
      return checkValueIsInRange(minValue ?? 0, maxValue ?? 0);
    } else {
      return Boolean(!utid);
    }
  };

  const handleSetUtid = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVariableValue("");
    setInputWarning("");
    setUtid(e.target.value);
  };

  const handleAddMaxAffordable = async (amount: number) => {
    setSubmitting(true);
    const variableItem = card.items.find(
      (item) => item.valueType === BRAND_ITEM_VARIABLE_VALUE,
    );

    if (!variableItem) throw new Error("Variable item not found");

    const item: TangoProduct = {
      utid: variableItem.utid,
      amount: amount,
      type: BRAND_ITEM_VARIABLE_VALUE,
      metadata: card,
    };

    await addTangoItem(item);
    console.log("added max affordable to cart", item);
    navigate("/shop/giftCards");
  };

  const renderVariableInput = (
    item: TangoBrand["items"][number],
    onClick: () => void = () => null,
  ) => {
    return (
      <div>
        <Input
          onClick={onClick}
          error={Boolean(inputWarning)}
          margin="normal"
          id="variable-input"
          label="Amount"
          name="amount"
          onChange={handleVariableAmountChange(
            parseInt(item.minValue?.toString() ?? "0"),
            parseInt(item.maxValue?.toString() ?? "0"),
            (value) => setVariableValue(value),
            (value) => setInputWarning(value),
          )}
          data-cypress="gift-card-amount-input"
          value={variableValue}
          helperText={inputWarning}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <div>Points - {Math.round(Number(variableValue) * 5)}</div>
      </div>
    );
  };

  const renderItems = () => {
    if (hasFixedValueItem) {
      return (
        <FormControl>
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={utid}
            onChange={handleSetUtid}
          >
            {card.items
              .sort((a, b) => (a.faceValue ?? 0) - (b.faceValue ?? 0))
              .map((item) => {
                if (item.valueType === BRAND_ITEM_VARIABLE_VALUE) {
                  return (
                    <div key={item.utid} style={{ marginBottom: 8 }}>
                      <FormControlLabel
                        value={item.utid}
                        control={<Radio color="secondary" />}
                        label={renderVariableInput(item, () =>
                          setUtid(item.utid),
                        )}
                      />
                    </div>
                  );
                } else if (item.valueType === BRAND_ITEM_FIXED_VALUE) {
                  return (
                    <div key={item.utid} style={{ marginBottom: 8 }}>
                      <FormControlLabel
                        value={item.utid}
                        control={<Radio color="secondary" />}
                        label={`$${item.faceValue} - ${item.rewardName} - (${convertToPoints(
                          item.faceValue ?? 0,
                        )} Points)`}
                      />
                    </div>
                  );
                } else {
                  return null;
                }
              })}
          </RadioGroup>
        </FormControl>
      );
    }

    if (hasOnlyVariableValue) {
      const item = card.items[0];
      return renderVariableInput(item);
    }

    return (
      <div style={{ color: "red" }}>This brand is currently not supported</div>
    );
  };

  const maxValue =
    card.items.find((item) => item.valueType === BRAND_ITEM_VARIABLE_VALUE)
      ?.maxValue ?? 0;

  if (card) {
    return (
      <Box data-cypress="gift-card-info-page" sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <div data-cypress="gift-card-details">
              <CardImage
                imageUrl={card.imageUrls["300w-326ppi"]}
                height={"190px"}
                width={"300px"}
                margin={{ xs: "5px 5px 0 3px", sm: "auto" }}
              />
              <div style={{ fontWeight: "bold" }}>{card.brandName}</div>
              <div
                style={{ marginTop: 30 }}
                dangerouslySetInnerHTML={{ __html: card.description }}
              ></div>
              <div
                style={{ marginTop: 30 }}
                dangerouslySetInnerHTML={{ __html: card.disclaimer }}
              ></div>
            </div>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <div data-cypress="gift-card-purchase-options">
              <div style={{ fontWeight: "bold", marginBottom: 6 }}>
                Select Amount
              </div>
              {hasVariableValueItem && (
                <AddMaxValueButton
                  maxValue={maxValue}
                  onAdd={handleAddMaxAffordable}
                  disabled={submitting}
                />
              )}
              {renderItems()}
              <div style={{ marginTop: 30 }}></div>
              <div>
                {Array.isArray(card.checks) &&
                  card.checks.map((check: any, index: number) => (
                    <div key={index} style={{ color: "red", marginBottom: 10 }}>
                      {check.description}
                    </div>
                  ))}
                <Button
                  data-cypress="add-to-cart-button"
                  onClick={handleSubmit}
                  sx={{ color: "white" }}
                  disabled={isSubmitDisabled() || submitting}
                >
                  Add To Cart
                </Button>
              </div>
            </div>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return null;
};
